import React, {useState, useEffect} from 'react';
import {TouchableOpacity} from 'react-native';
import OtpInput from 'react-otp-input';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import CCButton from '@/Atoms/CCButton';
import Spacer from '@/Atoms/Spacer';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {sendEmailChangeOtp, verifyEmailChangeOtp} from '@/Hooks/ProfileApi/useEmailChangeApi';

type Step = 'cooldown' | 'otp_rate_limit' | 'enter_email' | 'enter_otp' | 'success' | 'error';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: (newEmail: string) => void;
  emailLastChangedAt?: string | null;
}

export default function EmailChangeModal({visible, onClose, onSuccess, emailLastChangedAt}: Props) {
  const {isMobileOrTab} = useMediaQuery();

  const computeInitialStep = (): Step => {
    if (!emailLastChangedAt) return 'enter_email';
    const elapsed = (Date.now() - new Date(emailLastChangedAt).getTime()) / 1000;
    return elapsed < 48 * 3600 ? 'cooldown' : 'enter_email';
  };

  const computeCooldownMessage = (): string => {
    if (!emailLastChangedAt) return '';
    const elapsed = (Date.now() - new Date(emailLastChangedAt).getTime()) / 1000;
    const remaining = Math.max(0, Math.ceil(48 * 3600 - elapsed));
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    if (hours > 0) return `You can update your email again in ${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}.`;
    return `You can update your email again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`;
  };

  const [step, setStep] = useState<Step>(computeInitialStep);

  useEffect(() => {
    if (visible) {
      setStep(computeInitialStep());
    }
  }, [visible]);
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [rateLimitMsg, setRateLimitMsg] = useState('');

  const reset = () => {
    setStep(computeInitialStep());
    setNewEmail('');
    setOtp('');
    setErrorMsg('');
    setLoading(false);
    setResendCooldown(0);
    setRateLimitMsg('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const startResendTimer = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {clearInterval(interval); return 0;}
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) {setErrorMsg('Please enter a valid email address.'); return;}
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await sendEmailChangeOtp(trimmed);
      if (res.success) {
        setStep('enter_otp');
        startResendTimer();
      } else if (res.error === 'cooldown') {
        setStep('cooldown');
      } else if (res.error === 'otp_rate_limit') {
        setRateLimitMsg(res.message || 'Too many OTP requests. Please try again later.');
        setStep('otp_rate_limit');
      } else if (res.error === 'email_already_taken') {
        setErrorMsg('This email is already associated with another account.');
      } else if (res.error === 'new email is same as current email') {
        setErrorMsg('This is already your current email address.');
      } else if (res.error === 'otp_send_failed') {
        setErrorMsg('Could not send OTP. Please try again.');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {setErrorMsg('Please enter the OTP.'); return;}
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await verifyEmailChangeOtp(newEmail.trim().toLowerCase(), otp.trim());
      if (res.success) {
        setStep('success');
      } else if (res.error === 'otp_expired') {
        setErrorMsg('OTP expired. Please request a new one.');
        setStep('enter_email');
      } else if (res.error === 'invalid_otp') {
        setErrorMsg('Incorrect OTP. Please try again.');
      } else if (res.error === 'cooldown') {
        setErrorMsg(res.message || 'Email was recently updated. Please wait before trying again.');
      } else {
        setErrorMsg('Email update failed. Our team has been notified.');
        setStep('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await sendEmailChangeOtp(newEmail.trim().toLowerCase());
      if (res.success) {
        startResendTimer();
      } else if (res.error === 'otp_rate_limit') {
        setRateLimitMsg(res.message || 'Too many OTP requests. Please try again later.');
        setStep('otp_rate_limit');
      } else {
        setErrorMsg(res.error || 'Could not resend OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideModal
      visible={visible}
      onClose={handleClose}
      type="center"
      modalHeaderTitle={step === 'success' ? 'Email Updated' : 'Update Email Address'}
      minWidth={1}
      contentPadding={16}
      customStyle={{width: isMobileOrTab ? '90%' : 360}}>
      <Box style={{paddingBottom: vs(8)}}>

        <Spacer spacing={vs(8)} />

        {step === 'cooldown' && (
          <>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor?.Grey} style={{textAlign: 'center'}}>
              You recently updated your email address.{'\n\n'}
              {computeCooldownMessage()}{'\n\n'}
              For security, email changes are limited to once every 48 hours.
            </CCText>
            <Spacer spacing={vs(20)} />
            <CCButton onPress={handleClose}>
              <CCText>Close</CCText>
            </CCButton>
          </>
        )}

        {step === 'otp_rate_limit' && (
          <>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor?.Grey} style={{textAlign: 'center'}}>
              You have requested too many OTPs.{'\n\n'}
              {rateLimitMsg}{'\n\n'}
              For security, you can only request 3 OTPs per hour.
            </CCText>
            <Spacer spacing={vs(20)} />
            <CCButton onPress={handleClose}>
              <CCText>Close</CCText>
            </CCButton>
          </>
        )}

        {step === 'enter_email' && (
          <>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor?.Grey}>
              Enter the new email address you want to use. (For security purposes, you will not be able to change your email again for 48 hours after updating.)
            </CCText>
            <Spacer spacing={vs(12)} />
            <CCTextInput
              label="New Email Address"
              value={newEmail}
              onChangeText={text => {setNewEmail(text); setErrorMsg('');}}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            {!!errorMsg && (
              <>
                <Spacer spacing={vs(8)} />
                <CCText fontSize={FontSizes[13]} color={CCColors.Red || '#e53935'}>
                  {errorMsg}
                </CCText>
              </>
            )}
            <Spacer spacing={vs(16)} />
            <CCButton onPress={handleSendOtp} disabled={loading}>
              <CCText>{loading ? 'Sending...' : 'Send OTP'}</CCText>
            </CCButton>
          </>
        )}

        {step === 'enter_otp' && (
          <>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor?.Grey}>
              OTP sent to{' '}
              <CCText fontSize={FontSizes[14]} fontWeight="semibold">
                {newEmail}
              </CCText>
              . Enter it below.
            </CCText>
            <Spacer spacing={vs(12)} />
            <OtpInput
                value={otp}
                onChange={text => {setOtp(text); setErrorMsg('');}}
                numInputs={4}
                shouldAutoFocus
                inputType="tel"
                containerStyle={{display: 'flex', justifyContent: 'center', width: '100%'}}
                renderSeparator={<span style={{width: 8}} />}
                inputStyle={{
                  width: 44,
                  height: 52,
                  border: '1.5px solid #D0D5DD',
                  borderRadius: 8,
                  textAlign: 'center',
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: '#101828',
                  outline: 'none',
                  backgroundColor: '#F9FAFB',
                }}
                renderInput={props => (
                  <input
                    {...props}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && otp.length === 4) {
                        e.preventDefault();
                        handleVerifyOtp();
                        return;
                      }
                      if (e.key.length === 1 && !/\d/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        return;
                      }
                      if (props.onKeyDown) props.onKeyDown(e);
                    }}
                  />
                )}
              />
            {!!errorMsg && (
              <>
                <Spacer spacing={vs(8)} />
                <CCText fontSize={FontSizes[13]} color={CCColors.Red || '#e53935'}>
                  {errorMsg}
                </CCText>
              </>
            )}
            <Spacer spacing={vs(16)} />
            <CCButton onPress={handleVerifyOtp} disabled={loading}>
              <CCText>{loading ? 'Verifying...' : 'Verify & Update'}</CCText>
            </CCButton>
            <Spacer spacing={vs(12)} />
            <TouchableOpacity onPress={handleResend} disabled={resendCooldown > 0 || loading}>
              <CCText
                fontSize={FontSizes[13]}
                style={{textAlign: 'center'}}
                color={resendCooldown > 0 ? CCColors.TextColor?.Grey : CCColors.Primary}>
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </CCText>
            </TouchableOpacity>
          </>
        )}

        {step === 'success' && (
          <>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.TextColor?.Grey}
              style={{textAlign: 'center'}}>
              Your email has been successfully updated to{' '}
              <CCText fontSize={FontSizes[14]} fontWeight="semibold">
                {newEmail}
              </CCText>
              .
            </CCText>
            <Spacer spacing={vs(20)} />
            <CCButton onPress={() => {onSuccess(newEmail.trim().toLowerCase()); handleClose();}}>
              <CCText>Done</CCText>
            </CCButton>
          </>
        )}

        {step === 'error' && (
          <>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.Red || '#e53935'}
              style={{textAlign: 'center'}}>
              {errorMsg || 'Email update failed. Our team has been notified.'}
            </CCText>
            <Spacer spacing={vs(20)} />
            <CCButton onPress={handleClose}>
              <CCText>Close</CCText>
            </CCButton>
          </>
        )}
      </Box>
    </SlideModal>
  );
}
